import React, { useState, useEffect } from "react";
import ModalWrapper from "../../../../../Components/Modals/ModalWrapper";
import { FaPlus } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { assignDriver } from "../../../../../services/shipments.services";
import { showSnackBar } from "../../../../../store/common/snackBarSlice";
import DriverForm from "../../../Drivers/Components/DriverForm/DriverForm";
import AutoComplete from "../../../../../Components/UI/Inputs/AutoComplete";
import { useTranslation } from "react-i18next";

const DriverSkeleton = () => {
  return (
    <div className="animate-pulse space-y-4">
      {[1, 2].map((item) => (
        <div key={item}>
          <div className="h-4 w-24 bg-ui-border rounded mb-2" />
          <div className="h-10 w-full bg-ui-border rounded-lg" />
          <div className="h-4 w-32 bg-ui-border mt-2 rounded" />
        </div>
      ))}
    </div>
  );
};

const SelectDriver = ({
  open,
  setOpen,
  drivers,
  setShipments,
  getAllDrivers,
}) => {
  const { t } = useTranslation("carrier");
  const dispatch = useDispatch();
  const [selectedPickupDriver, setSelectedPickupDriver] = useState("");
  const [selectedDeliveryDriver, setSelectedDeliveryDriver] = useState("");
  const [loading, setLoading] = useState(false);
  const [openDriverModal, setOpenDriverModal] = useState(null);
  const [driverOptions, setDriverOptions] = useState([]);
  const [modalLoading, setModalLoading] = useState();

  // Pre-select drivers when modal opens
  useEffect(() => {
    if (open) {
      setSelectedPickupDriver(open?.pickup_driver || "");
      setSelectedDeliveryDriver(open?.delivery_driver || "");
    }
  }, [open]);

  const handleClose = () => setOpen(false);

  const handleSave = async () => {
    if (!selectedPickupDriver && !selectedDeliveryDriver) return;

    setLoading(true);

    const response = await assignDriver(
      open?.shipmentId,
      selectedPickupDriver,
      selectedDeliveryDriver
    );

    if (response.error) {
      dispatch(
        showSnackBar({
          text: response?.error ?? t("common:messages.somethingWentWrong"),
          severity: "error",
        })
      );
    } else {
      handleClose();
      setShipments((shipments) => ({
        ...shipments,
        data: shipments?.data?.map((shipment) =>
          shipment.id === open.shipmentId
            ? { ...shipment, ...response.data.result }
            : shipment
        ),
      }));

      dispatch(
        showSnackBar({
          text: t("dispatcher.pickupDeliveryDriversUpdated"),
          severity: "success",
        })
      );
    }

    setLoading(false);
  };

  useEffect(() => {
    if (drivers?.length) {
      setDriverOptions(
        drivers.map((driver) => ({
          value: driver.id,
          label: driver.name,
        }))
      );
    }
  }, [drivers]);
  useEffect(() => {}, [driverOptions]);

  return (
    <ModalWrapper
      isOpen={open}
      onClose={handleClose}
      boxClassName="w-96 p-6 rounded-2xl shadow-xl bg-ui-surface"
    >
      <DriverForm
        open={!!openDriverModal}
        setOpen={() => setOpenDriverModal(null)}
        setRefresh={async (trigger, response) => {
          const data = response?.data?.result;
          setModalLoading(true);

          await getAllDrivers(1, 100, "");
          setModalLoading(false);
          if (openDriverModal === "pickup") {
            setSelectedPickupDriver(data?.id);
          } else if (openDriverModal === "delivery") {
            setSelectedDeliveryDriver(data?.id);
          }
        }}
      />

      <h2 className="text-xl font-jakarta font-semibold text-ui-text mb-4">
        {t("dispatcher.selectDrivers")}
      </h2>

      {modalLoading ? (
        <DriverSkeleton />
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium mb-1">
              {t("dispatcher.pickupDriver")}
            </label>
            <div className="flex items-center space-x-2">
              <div className="flex-1">
                <AutoComplete
                  options={driverOptions}
                  selected={parseInt(selectedPickupDriver)}
                  setSelected={setSelectedPickupDriver}
                  placeholder={t("dispatcher.selectPickupDriver")}
                />
              </div>
              <button
                onClick={() => setOpenDriverModal("pickup")}
                className="p-2 bg-brand text-white rounded-full hover:bg-brand-hover transition-all duration-150 shadow-soft-sm"
              >
                <FaPlus className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">
              {t("dispatcher.deliveryDriver")}
            </label>
            <div className="flex items-center space-x-2">
              <div className="flex-1">
                <AutoComplete
                  options={driverOptions}
                  selected={parseInt(selectedDeliveryDriver)}
                  setSelected={setSelectedDeliveryDriver}
                  placeholder={t("dispatcher.selectDeliveryDriver")}
                />
              </div>
              <button
                onClick={() => setOpenDriverModal("delivery")}
                className="p-2 bg-brand text-white rounded-full hover:bg-brand-hover transition-all duration-150 shadow-soft-sm"
              >
                <FaPlus className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end mt-6 space-x-3">
        <button
          className="w-20 text-sm flex justify-center py-1.5 text-ui-text bg-ui-border rounded-lg hover:bg-ui-border transition-all duration-200"
          onClick={handleClose}
        >
          {t("common:actions.cancel")}
        </button>
        <button
          className={`w-20 text-sm flex justify-center py-1.5 text-white rounded-lg transition-all duration-200 ${
            loading || (!selectedPickupDriver && !selectedDeliveryDriver)
              ? "bg-ui-text-subtle cursor-not-allowed"
              : "bg-brand hover:bg-brand-hover"
          }`}
          onClick={handleSave}
          disabled={
            loading || (!selectedPickupDriver && !selectedDeliveryDriver)
          }
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          ) : (
            t("common:actions.save")
          )}
        </button>
      </div>
    </ModalWrapper>
  );
};

export default SelectDriver;
